import { apiFetch, type PaginatedResponse } from './api';

export interface ProposedBump {
  name: string;
  repository: string;
  fromVersion: string | null;
  toVersion: string;
}

export interface SkippedBump {
  name: string;
  repository: string;
  currentVersion: string;
  latestAvailable: string;
  reason?: string;
}

export interface FileModSnapshot {
  path: string;
  type: 'added' | 'modified' | 'deleted';
  kind: 'file' | 'folder';
  content?: string;
  size?: string;
  by?: string;
}

export interface ManualBump {
  name: string;
  toVersion: string;
}

export interface ReleaseError {
  at: string;
  phase: string;
  message: string;
}

export type ReleaseStatus =
  | 'update-pending'
  | 'update-failed'
  | 'review-required'
  | 'applying'
  | 'applied'
  | 'pr-merged'
  | 'released'
  | 'cancelled'
  | 'failed';

export interface ContainerRelease {
  id: string;
  containerId: string;
  containerCode: string;
  projectId: string;
  branchName: string;
  baseRef: string;
  baseCommitSha?: string;

  initiatedBy: string;
  initiatedByName: string;
  initiatedAt: string;

  requestedFileModPaths?: string[];
  fileModSnapshots?: FileModSnapshot[];

  branchPushedAt?: string;
  updateWorkflowRunId?: number;
  updateWorkflowStatus?: string;
  updateWorkflowConclusion?: string;
  proposedBumps?: ProposedBump[];
  skippedBumps?: SkippedBump[];
  proposedAt?: string;

  acceptedBumpNames?: string[];
  manualBumps?: ManualBump[];
  appliedFileModPaths?: string[];
  appliedAt?: string;
  appliedBy?: string;
  appliedByName?: string;
  curationCommitSha?: string;

  pullRequestNumber?: number;
  pullRequestUrl?: string;
  pullRequestState?: 'open' | 'closed' | 'merged';
  mergedAt?: string;
  mergedByLogin?: string;
  mergedWithoutCuration: boolean;

  releaseWorkflowRunId?: number;
  releaseWorkflowStatus?: string;
  releaseWorkflowConclusion?: string;
  tagged?: boolean;
  githubReleaseUrl?: string;
  dockerImage?: string;
  imagePublishedAt?: string;

  status: ReleaseStatus;
  errors?: ReleaseError[];
  updatedAt?: string;

  // Relances : bouton « Réessayer » (retryCount, lastRetry*) et relance automatique d'un run
  // GitHub annulé faute de runner, une seule fois par release (autoRetriedAt).
  retryCount?: number;
  lastRetryAt?: string;
  lastRetryByName?: string;
  /** rerun, rerun-failed-jobs, dispatch, ou resume (run GitHub encore en cours : suivi repris). */
  lastRetryMode?: 'rerun' | 'rerun-failed-jobs' | 'dispatch' | 'resume';
  autoRetriedAt?: string;
  /** Workflow relancé automatiquement : update.yml ou release.yml. */
  autoRetryWorkflow?: string;
}

/** Statuts d'échec qu'on peut relancer : update.yml (update-failed) ou release.yml (failed). */
export const RETRYABLE_STATUSES: readonly ReleaseStatus[] = ['update-failed', 'failed'];

const NON_TERMINAL_STATUSES: readonly ReleaseStatus[] = ['update-pending', 'review-required', 'applying', 'applied', 'pr-merged'];

/**
 * Même règle que le CICD (ContainerReleaseService.blocksRetry) : une release en échec se relance
 * sauf si une autre release du même container est en cours, ou si une version plus récente a
 * déjà été publiée. `siblings` : les autres releases connues du container (ordre libre).
 */
export function canRetryRelease(release: ContainerRelease, siblings: (ContainerRelease | null | undefined)[]): boolean {
  if (!RETRYABLE_STATUSES.includes(release.status)) return false;
  const at = new Date(release.initiatedAt).getTime();
  return !siblings.some((o) => {
    if (!o || o.id === release.id || o.containerCode !== release.containerCode) return false;
    if (NON_TERMINAL_STATUSES.includes(o.status)) return true;
    return o.status === 'released' && new Date(o.initiatedAt).getTime() > at;
  });
}

export interface AcceptedBump {
  name: string;
  toVersion: string;
}

export interface AutoApplyConfig {
  bumps: AcceptedBump[];
  autoMerge?: boolean;
  commitMessage?: string;
}

export interface CreateReleaseInput {
  newVersion: string;
  includedFileModPaths?: string[];
  commitMessage?: string;
  autoApply?: AutoApplyConfig;
}

export interface ApplyReleaseInput {
  acceptedBumpNames?: string[];
  manualBumps?: ManualBump[];
  appliedFileModPaths?: string[];
  commitMessage?: string;
  autoMerge?: boolean;   // défaut true côté backend
}

const baseUrl = (projectId: string, code: string) =>
  `/api/projects/${projectId}/containers/${code}/releases`;

export const containerReleasesService = {
  list: (projectId: string, code: string, limit = 20) =>
    apiFetch<ContainerRelease[]>(`${baseUrl(projectId, code)}?limit=${limit}`),

  /** Historique du PROJET, toutes containers confondues, paginé (qui / quand / quoi). */
  listByProject: (
    projectId: string,
    q: { page?: number; pageSize?: number; code?: string; status?: string } = {},
  ) => {
    const params = new URLSearchParams();
    params.set('page', String(q.page ?? 0));
    params.set('pageSize', String(q.pageSize ?? 50));
    if (q.code) params.set('code', q.code);
    if (q.status) params.set('status', q.status);
    return apiFetch<PaginatedResponse<ContainerRelease>>(
      `/api/projects/${projectId}/releases?${params.toString()}`,
    );
  },

  active: async (projectId: string, code: string): Promise<ContainerRelease | null> => {
    try {
      const r = await apiFetch<ContainerRelease | undefined>(`${baseUrl(projectId, code)}/active`);
      return r ?? null;
    } catch {
      return null;
    }
  },

  get: (projectId: string, code: string, releaseId: string) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}`),

  create: (projectId: string, code: string, input: CreateReleaseInput) =>
    apiFetch<ContainerRelease>(baseUrl(projectId, code), { method: 'POST', json: input }),

  apply: (projectId: string, code: string, releaseId: string, input: ApplyReleaseInput) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}/apply`, {
      method: 'POST',
      json: input,
    }),

  cancel: (projectId: string, code: string, releaseId: string) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}/cancel`, { method: 'POST' }),

  merge: (projectId: string, code: string, releaseId: string) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}/merge`, { method: 'POST' }),

  refresh: (projectId: string, code: string, releaseId: string) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}/refresh`, { method: 'POST' }),

  /** Relance une release en échec : re-run du workflow GitHub et reprise du suivi. */
  retry: (projectId: string, code: string, releaseId: string) =>
    apiFetch<ContainerRelease>(`${baseUrl(projectId, code)}/${releaseId}/retry`, { method: 'POST' }),
};
